package top.wcpe.allincore.dsl.ir.codec

import top.wcpe.allincore.dsl.ir.expr.IrType
import top.wcpe.allincore.dsl.ir.model.ModifierOpcodes
import top.wcpe.allincore.dsl.ir.model.ThemeDescriptor
import top.wcpe.allincore.dsl.ir.model.UiBinding
import top.wcpe.allincore.dsl.ir.model.UiCanvasData
import top.wcpe.allincore.dsl.ir.model.UiDerived
import top.wcpe.allincore.dsl.ir.model.UiIr
import top.wcpe.allincore.dsl.ir.model.UiInterceptRule
import top.wcpe.allincore.dsl.ir.model.UiModel
import top.wcpe.allincore.dsl.ir.model.UiModifier
import top.wcpe.allincore.dsl.ir.model.UiNode
import top.wcpe.allincore.dsl.ir.model.UiParam
import top.wcpe.allincore.dsl.ir.model.UiParams
import top.wcpe.allincore.dsl.ir.model.UiPlaceholderBlock
import top.wcpe.allincore.dsl.ir.model.UiScreenFont
import top.wcpe.allincore.dsl.ir.model.UiState
import top.wcpe.allincore.dsl.ir.model.UiSubscribeBlock
import top.wcpe.allincore.dsl.ir.model.action.IrFun
import top.wcpe.allincore.dsl.ir.model.action.UiAction
import top.wcpe.allincore.dsl.ir.model.animation.SpringSpec
import top.wcpe.allincore.dsl.ir.model.animation.UiAnimation
import top.wcpe.allincore.dsl.ir.model.animation.UiKeyframe
import top.wcpe.allincore.dsl.ir.model.hud.HudLayoutPolicy
import top.wcpe.allincore.dsl.ir.model.hud.HudStackPolicy
import top.wcpe.allincore.dsl.ir.model.hud.HudVisibilityPolicy
import top.wcpe.allincore.dsl.ir.model.hud.UiHudMeta
import top.wcpe.allincore.dsl.ir.model.hud.UiKeyBinding
import top.wcpe.allincore.dsl.ir.model.overlay.ContentRef
import top.wcpe.allincore.dsl.ir.model.overlay.OverlayDescriptor
import top.wcpe.allincore.dsl.ir.model.statemachine.UiSmState
import top.wcpe.allincore.dsl.ir.model.statemachine.UiSmTransition
import top.wcpe.allincore.dsl.ir.model.statemachine.UiStateMachine
import top.wcpe.allincore.dsl.ir.version.IrProtocolVersion
import kotlin.jvm.JvmStatic

/**
 * IR 二进制解码器。
 *
 * 负责将二进制字节流反序列化为 [UiIr] 模型，协议布局严格对齐
 * `docs/gui/M4-执行计划.md §2.3`。
 *
 * commonMain 化（FR-UI-020 M3）：由原 jvmMain Java 平移至 commonMain 纯 Kotlin，
 * 剥离 Netty Unpooled（挪到 jvmMain [IrCodec] 门面），改用平台中立 [IrByteReader]
 * 接口 + 纯 Kotlin [ByteArrayIrReader]，解码行为逐一等价。
 */
object IrDecoder {

    /** binding type 常量。 */
    const val BT_LITERAL: Int = 0
    const val BT_EXPR: Int = 1
    const val BT_EVENT: Int = 2

    /** themeTokens valueTag 常量（M7 多版本需保持一致）。 */
    const val TT_STRING: Int = 0
    const val TT_NUMBER: Int = 1
    const val TT_BOOL: Int = 2

    /** v4 wire 协议固定支持的最大 animation kind ordinal（PATH_TRANSITION=5）。 */
    private const val ANIMATION_KIND_V4_MAX = 5

    /** Inline 内容允许的最大嵌套层数，避免恶意 IR 触发栈溢出。 */
    private const val MAX_INLINE_DEPTH = 16

    /** 单个 Inline 内容编码后的最大字节数，避免 codec 层分配过大载荷。 */
    private const val MAX_INLINE_BYTES = 1024 * 1024

    /**
     * 节点 / 动画 children 递归允许的最大嵌套深度，避免恶意 IR 触发栈溢出。
     *
     * 取值 512：真实 UI 树（含设计稿与极端 dungeon/showcase 示例）层数都在两位数以内，
     * 留足充裕余量；同时远低于默认线程栈在 decodeNode 单帧开销下的溢出阈值，
     * 可在 StackOverflowError 之前以 IllegalStateException 明确拒绝病态字节流。
     */
    private const val MAX_NODE_DEPTH = 512

    private const val CR_TEXT = 0
    private const val CR_INLINE = 1
    private const val CR_COMPONENT = 2

    // ==================== 主入口 ====================

    /** 从平台中立字节读取器解码。JVM 门面用 ByteBufIrReader 桥接，其他目标用 ByteArrayIrReader。 */
    @JvmStatic
    fun decode(inp: IrByteReader): UiIr {
        return decode(inp, InlineDecodingGuard())
    }

    /** 便利入口：从 ByteArray 解码（内部用纯 Kotlin ByteArrayIrReader）。 */
    @JvmStatic
    fun decodeFromBytes(bytes: ByteArray): UiIr {
        return decodeFromBytes(bytes, InlineDecodingGuard())
    }

    private fun decode(inp: IrByteReader, guard: InlineDecodingGuard): UiIr {
        val magic = inp.readInt()
        val version = inp.readUnsignedShort()
        if (magic != IrProtocolVersion.MAGIC ||
            version > IrProtocolVersion.CURRENT.toInt() ||
            version < IrProtocolVersion.MIN_SUPPORTED.toInt()
        ) {
            throw IllegalStateException("IR magic/version mismatch")
        }

        val screenId = IrReader.readLpString(inp)

        // state entries
        val stateDefaults = decodeStateEntries(inp, version)

        // v24 新增：session / persist 默认值表（每段前置 u8 hasXxx）
        val sessionStateDefaults = decodeOptionalStateEntries(inp, version)
        val persistStateDefaults = decodeOptionalStateEntries(inp, version)

        // v25 新增：derived 派生值表（前置 u8 hasDerived）
        val derivedDefaults = decodeDerivedDefaults(inp, version)

        // actions
        val actions = decodeActions(inp, version)

        // themeTokens
        val themeTokens = decodeThemeTokens(inp)

        // availableThemes（v13 新增；低版本视为空表）
        val availableThemes = decodeAvailableThemes(inp, version)

        // v34 主题目录；v33 字节流没有该段，兼容为空目录。
        val themeDirectory = decodeThemeDirectory(inp, version)

        // animations
        val animations = decodeAnimations(inp, version, guard)

        // stateMachines（v8 新增；低版本视为空表）
        val stateMachines = decodeStateMachines(inp, version)

        // functions（v4 新增；低版本视为空表）
        val functions = decodeFunctions(inp, version)

        // placeholders（v17 新增；低版本视为空表）
        val placeholders = decodePlaceholders(inp, version)

        // subscribes（v21 新增；低版本视为空 list）
        val subscribes = decodeSubscribes(inp, version)

        // hud meta（v22 新增；低版本视为空 anchor=null / layer=null / hide=empty）
        val hudMeta = decodeHudMeta(inp, version)

        // 界面级字体（v27 新增；低版本无此段，返回 null）
        val screenFont = decodeScreenFont(inp, version)
        // Phase 3：intercept 路由规则（v28 新增；位于 screenFont 之后；低版本视为 null）
        val interceptRule = decodeInterceptRule(inp, version)
        // v33：DSL 文件头与 model 地基，位于 root 之前。
        val dslModel = decodeDslModel(inp, version)

        // root
        val root = decodeNode(inp, version, guard)

        // overlay tail（Stage C；旧字节流没有该段，直接视为空表）
        val overlayTail = decodeOverlayTail(inp, guard, version)

        var ir = UiIr(
            screenId, stateDefaults, actions, themeTokens,
            availableThemes, animations, functions, stateMachines,
            placeholders, overlayTail.overlayDescriptors, overlayTail.elementOverlays,
            subscribes, root,
            hudMeta,
        )
            .withThemeDirectory(themeDirectory)
            .withSessionStateDefaults(sessionStateDefaults)
            .withPersistStateDefaults(persistStateDefaults)
            .withDerivedDefaults(derivedDefaults)
            .withDslHeader(dslModel.dslVersion, dslModel.dslMode)
            .withModel(dslModel.model)
            .withInterceptRule(interceptRule)
        if (screenFont != null) {
            ir = ir.withScreenFont(screenFont)
        }
        return ir
    }

    // ==================== 分段解码 ====================

    private fun decodeStateEntries(inp: IrByteReader, version: Int): UiState {
        val stateCount = inp.readUnsignedShort()
        val state = LinkedHashMap<String, Any?>(stateCount)
        val stateTypes = LinkedHashMap<String, IrType>()
        for (i in 0 until stateCount) {
            val key = IrReader.readLpString(inp)
            val value = IrReader.readValue(inp)
            state[key] = value
            // M5-c #I5：读取类型标注标志位。MIN_SUPPORTED=10 起该字节恒存在。
            val hasType = inp.readUnsignedByte()
            if (hasType != 0) {
                val ord = inp.readUnsignedByte()
                stateTypes[key] = IrType.fromOrdinal(ord)
            }
        }
        return UiState(state, stateTypes)
    }

    private fun decodeDslModel(inp: IrByteReader, version: Int): DslModelSection {
        if (version < 33) {
            return DslModelSection.legacy()
        }
        val dslVersion = inp.readUnsignedShort()
        val dslMode = IrReader.readLpString(inp)
        val params = decodeParams(inp)
        val sessionNamespaces = decodeNamespaceStates(inp, version)
        val persistNamespaces = decodeNamespaceStates(inp, version)
        return DslModelSection(
            dslVersion,
            dslMode,
            UiModel(params, sessionNamespaces, persistNamespaces),
        )
    }

    private fun decodeParams(inp: IrByteReader): UiParams {
        val count = inp.readUnsignedShort()
        val entries = LinkedHashMap<String, UiParam>(count)
        for (i in 0 until count) {
            val name = IrReader.readLpString(inp)
            val type = IrType.fromOrdinal(inp.readUnsignedByte())
            val hasDefault = inp.readUnsignedByte() != 0
            val defaultValue = if (hasDefault) IrReader.readValue(inp) else null
            entries[name] = UiParam(name, type, defaultValue, hasDefault)
        }
        return UiParams(entries)
    }

    private fun decodeNamespaceStates(inp: IrByteReader, version: Int): Map<String, UiState> {
        val count = inp.readUnsignedShort()
        val out = LinkedHashMap<String, UiState>(count)
        for (i in 0 until count) {
            val namespace = IrReader.readLpString(inp)
            out[namespace] = decodeStateEntries(inp, version)
        }
        return out
    }

    private class DslModelSection(
        val dslVersion: Int,
        val dslMode: String,
        val model: UiModel,
    ) {
        companion object {
            fun legacy(): DslModelSection = DslModelSection(1, "legacy", UiModel.empty())
        }
    }

    /**
     * v24：可选 state 段解码 —— 读取 u8 hasXxx，hasXxx=1 时再读一份 stateEntries 段。
     *
     * 低于 v24 的字节流没有该段，调用方在 [decode] 中会基于 version 决定是否调用。
     */
    private fun decodeOptionalStateEntries(inp: IrByteReader, version: Int): UiState {
        if (version < 24) {
            return UiState.empty()
        }
        val has = inp.readUnsignedByte()
        if (has == 0) {
            return UiState.empty()
        }
        if (has != 1) {
            throw IllegalStateException("非法 hasOptionalStateEntries 标记: " + has)
        }
        return decodeStateEntries(inp, version)
    }

    /**
     * v25：derived 派生值段解码。低于 v25 的字节流没有该段，返回 [UiDerived.empty]。
     */
    private fun decodeDerivedDefaults(inp: IrByteReader, version: Int): UiDerived {
        if (version < 25) {
            return UiDerived.empty()
        }
        val has = inp.readUnsignedByte()
        if (has == 0) {
            return UiDerived.empty()
        }
        if (has != 1) {
            throw IllegalStateException("非法 hasDerived 标记: " + has)
        }
        val count = inp.readUnsignedShort()
        val exprs = LinkedHashMap<String, ByteArray>(count)
        val types = LinkedHashMap<String, IrType>()
        for (i in 0 until count) {
            val name = IrReader.readLpString(inp)
            val blob = IrReader.readLpBytes32(inp)
            exprs[name] = blob
            val hasType = inp.readUnsignedByte()
            if (hasType != 0) {
                val ord = inp.readUnsignedByte()
                types[name] = IrType.fromOrdinal(ord)
            }
        }
        return UiDerived(exprs, types)
    }

    private fun decodeActions(inp: IrByteReader, version: Int): Map<String, UiAction> {
        val actionCount = inp.readUnsignedShort()
        val actions = LinkedHashMap<String, UiAction>(actionCount)
        for (i in 0 until actionCount) {
            val name = IrReader.readLpString(inp)
            // v8: 读取 u8 kind；v7 字节流不含此字节
            var kind = UiAction.Kind.BLOCK
            if (version >= 8) {
                val kindOrdinal = inp.readUnsignedByte()
                val kinds = UiAction.Kind.values()
                if (kindOrdinal >= kinds.size) {
                    throw IllegalStateException("未知 action kind: " + kindOrdinal)
                }
                kind = kinds[kindOrdinal]
            }
            val body = IrReader.readLpBytes32(inp)
            actions[name] = UiAction(name, kind, body)
        }
        return actions
    }

    private fun decodeThemeTokens(inp: IrByteReader): Map<String, Any?> {
        val themeCount = inp.readUnsignedShort()
        val themeTokens = LinkedHashMap<String, Any?>(themeCount)
        for (i in 0 until themeCount) {
            val key = IrReader.readLpString(inp)
            themeTokens[key] = decodeThemeValue(inp)
        }
        return themeTokens
    }

    private fun decodeAvailableThemes(inp: IrByteReader, version: Int): Map<String, Map<String, Any?>> {
        if (version >= 13) {
            val schemeCount = inp.readUnsignedShort()
            val availableThemes = LinkedHashMap<String, Map<String, Any?>>(schemeCount)
            for (i in 0 until schemeCount) {
                val scheme = IrReader.readLpString(inp)
                val tokenCount = inp.readUnsignedShort()
                val tokens = LinkedHashMap<String, Any?>(tokenCount)
                for (j in 0 until tokenCount) {
                    val tk = IrReader.readLpString(inp)
                    tokens[tk] = decodeThemeValue(inp)
                }
                availableThemes[scheme] = tokens
            }
            return availableThemes
        } else {
            return emptyMap()
        }
    }

    private fun decodeThemeDirectory(inp: IrByteReader, version: Int): List<ThemeDescriptor> {
        if (version < 34) {
            return emptyList()
        }
        val count = inp.readUnsignedShort()
        val directory = ArrayList<ThemeDescriptor>(count)
        val ids = HashSet<String>(count)
        for (i in 0 until count) {
            val id = IrReader.readLpString(inp)
            if (!ids.add(id)) {
                throw IllegalStateException("themeDirectory 存在重复 id: " + id)
            }
            directory.add(
                ThemeDescriptor(
                    id,
                    IrReader.readLpString(inp),
                    IrReader.readLpString(inp),
                    inp.readInt(),
                    IrReader.readLpString(inp),
                    IrReader.readLpString(inp),
                    IrReader.readLpString(inp),
                ),
            )
        }
        return directory
    }

    private fun decodeAnimations(inp: IrByteReader, version: Int, guard: InlineDecodingGuard): Map<String, UiAnimation> {
        val animationCount = inp.readUnsignedShort()
        val animations = LinkedHashMap<String, UiAnimation>(animationCount)
        for (i in 0 until animationCount) {
            val id = IrReader.readLpString(inp)
            animations[id] = decodeAnimation(inp, version, guard)
        }
        return animations
    }

    private fun decodeStateMachines(inp: IrByteReader, version: Int): Map<String, UiStateMachine> {
        if (version >= 8) {
            val smCount = inp.readUnsignedShort()
            val stateMachines = LinkedHashMap<String, UiStateMachine>(smCount)
            for (i in 0 until smCount) {
                val id = IrReader.readLpString(inp)
                stateMachines[id] = decodeStateMachine(id, inp)
            }
            return stateMachines
        } else {
            return emptyMap()
        }
    }

    private fun decodeFunctions(inp: IrByteReader, version: Int): Map<String, IrFun> {
        if (version >= 4) {
            val fnCount = inp.readUnsignedShort()
            val functions = LinkedHashMap<String, IrFun>(fnCount)
            for (i in 0 until fnCount) {
                val name = IrReader.readLpString(inp)
                functions[name] = decodeFunction(name, inp)
            }
            return functions
        } else {
            return emptyMap()
        }
    }

    private fun decodePlaceholders(inp: IrByteReader, version: Int): Map<String, UiPlaceholderBlock> {
        if (version < 17) {
            return emptyMap()
        }
        val placeholderCount = inp.readUnsignedShort()
        val placeholders = LinkedHashMap<String, UiPlaceholderBlock>(placeholderCount)
        for (i in 0 until placeholderCount) {
            val shortName = IrReader.readLpString(inp)
            val refreshMs = inp.readUnsignedInt()
            val bindState = readOptionalString(inp)
            val pipeExprBytes = readOptionalBytes(inp)
            val onChange = readOptionalString(inp)
            val available = inp.readUnsignedByte() != 0
            placeholders[shortName] = UiPlaceholderBlock(
                shortName, refreshMs, bindState, pipeExprBytes, onChange, available,
            )
        }
        return placeholders
    }

    /**
     * v21 新增：subscribes 段解码（顶层 subscribe 块编译产物）。
     *
     * 低版本 IR 不含本段，直接返回空 list。wire 形态见 [IrEncoder.encodeSubscribes]。
     */
    private fun decodeSubscribes(inp: IrByteReader, version: Int): List<UiSubscribeBlock> {
        if (version < 21) {
            return emptyList()
        }
        val count = inp.readUnsignedShort()
        val subscribes = ArrayList<UiSubscribeBlock>(count)
        for (i in 0 until count) {
            val namespace = IrReader.readLpString(inp)
            val topic = IrReader.readLpString(inp)
            val paramName = IrReader.readLpString(inp)
            val bodyLen = inp.readInt()
            if (bodyLen < 0) {
                throw IllegalStateException("subscribe bodyBytes 长度非法: " + bodyLen)
            }
            // 防御畸形长度：超过单段上限或超过剩余可读字节，均在分配数组前拒绝，避免 OOM。
            if (bodyLen > MAX_INLINE_BYTES) {
                throw IllegalStateException(
                    "subscribe bodyBytes 长度超过上限: " + bodyLen + " > " + MAX_INLINE_BYTES,
                )
            }
            val readable = inp.readableBytes()
            if (bodyLen > readable) {
                throw IllegalStateException(
                    "subscribe bodyBytes 长度超过剩余可读字节: 声明 " + bodyLen + "，实际可读 " + readable,
                )
            }
            val bodyBytes = ByteArray(bodyLen)
            inp.readBytes(bodyBytes)
            subscribes.add(UiSubscribeBlock(namespace, topic, paramName, bodyBytes))
        }
        return subscribes
    }

    /**
     * v22 新增：hud meta 段解码（hud 块顶层 anchor / layer / hide 编译产物）。
     *
     * 低版本 IR 不含本段，直接返回空 [UiHudMeta]（anchor / layer = null，hide = empty）。
     * wire 形态见 [IrEncoder.encodeHudMeta]。
     */
    private fun decodeHudMeta(inp: IrByteReader, version: Int): UiHudMeta {
        if (version < 22) {
            return UiHudMeta.EMPTY
        }
        val has = inp.readUnsignedByte()
        if (has == 0) {
            return UiHudMeta.EMPTY
        }
        if (has != 1) {
            throw IllegalStateException("非法 hasHudMeta 标记: " + has)
        }
        val anchor = readOptionalString(inp)
        val layer = readOptionalString(inp)
        val hide = readStringList(inp)
        if (version < 23) {
            return UiHudMeta.legacy(anchor, layer, hide)
        }
        val zIndex = inp.readInt()
        val layoutPolicy = decodeHudLayoutPolicy(inp)
        val stackPolicy = decodeHudStackPolicy(inp)
        val visibilityPolicy = decodeHudVisibilityPolicy(inp)
        val tickBodyBytes = readOptionalBytes(inp)
        // Phase 4：解码 keybinding 列表（v26 新增；低版本视为空列表）
        val keyBindings = decodeKeyBindings(inp, version)
        return UiHudMeta(
            anchor, layer, zIndex, hide,
            layoutPolicy, stackPolicy, visibilityPolicy, tickBodyBytes, keyBindings,
        )
    }

    /**
     * Phase 4：解码 keybinding 列表。
     * 格式：u16 count + 重复 { LP-UTF8 id + optString defaultKey + optString label + optBytes onTriggerBytes }
     * 低版本 IR 不含该段，返回空列表。
     */
    private fun decodeKeyBindings(inp: IrByteReader, version: Int): List<UiKeyBinding> {
        if (version < 28) {
            return emptyList()
        }
        val count = inp.readUnsignedShort()
        if (count == 0) {
            return emptyList()
        }
        val list = ArrayList<UiKeyBinding>(count)
        for (i in 0 until count) {
            val id = IrReader.readLpString(inp)
            val defaultKey = readOptionalString(inp)
            val label = readOptionalString(inp)
            val onTriggerBytes = readOptionalBytes(inp)
            list.add(UiKeyBinding(id, defaultKey, label, onTriggerBytes))
        }
        return list
    }

    /**
     * Phase 3：解码 intercept 路由规则。
     * 格式：u8 hasRule + (hasRule=1 时 LP-UTF8 screenId + LP-UTF8 menuType + optBytes conditionBytes)
     * 低版本 IR 不含本段，直接返回 null。
     */
    private fun decodeInterceptRule(inp: IrByteReader, version: Int): UiInterceptRule? {
        if (version < 28) {
            return null
        }
        val has = inp.readUnsignedByte()
        if (has == 0) {
            return null
        }
        if (has != 1) {
            throw IllegalStateException("非法 hasInterceptRule 标记: " + has)
        }
        val screenId = IrReader.readLpString(inp)
        val menuType = IrReader.readLpString(inp)
        val conditionBytes = readOptionalBytes(inp)
        return UiInterceptRule(screenId, menuType, conditionBytes)
    }

    /**
     * v27 新增：界面级字体段解码。
     *
     * 低版本 IR 不含本段，返回 `null`。wire 形态见 [IrEncoder.encodeScreenFont]：
     * u8 hasFont；=1 时 可空 name + 三个三态 Boolean（shadow / bold / italic）。
     */
    private fun decodeScreenFont(inp: IrByteReader, version: Int): UiScreenFont? {
        if (version < 27) {
            return null
        }
        val has = inp.readUnsignedByte()
        if (has == 0) {
            return null
        }
        if (has != 1) {
            throw IllegalStateException("非法 hasScreenFont 标记: " + has)
        }
        val name = readOptionalString(inp)
        val shadow = readTriState(inp)
        val bold = readTriState(inp)
        val italic = readTriState(inp)
        return UiScreenFont(name, shadow, bold, italic)
    }

    /** 三态 Boolean 解码：0→false，1→true，2→null。 */
    private fun readTriState(inp: IrByteReader): Boolean? {
        val v = inp.readUnsignedByte()
        return when (v) {
            0 -> false
            1 -> true
            2 -> null
            else -> throw IllegalStateException("非法三态 Boolean: " + v)
        }
    }

    private fun decodeHudLayoutPolicy(inp: IrByteReader): HudLayoutPolicy {
        val offsetX = inp.readInt()
        val offsetY = inp.readInt()
        val safeArea = inp.readUnsignedByte() != 0
        val avoidVanilla = inp.readUnsignedByte() != 0
        return HudLayoutPolicy(offsetX, offsetY, safeArea, avoidVanilla)
    }

    private fun decodeHudStackPolicy(inp: IrByteReader): HudStackPolicy {
        val group = readOptionalString(inp)
        val direction = IrReader.readLpString(inp)
        val gap = inp.readInt()
        val order = inp.readInt()
        return HudStackPolicy(group, direction, gap, order)
    }

    private fun decodeHudVisibilityPolicy(inp: IrByteReader): HudVisibilityPolicy {
        val whenScreen = IrReader.readLpString(inp)
        val allow = readStringList(inp)
        val deny = readStringList(inp)
        return HudVisibilityPolicy(whenScreen, allow, deny)
    }

    private fun readStringList(inp: IrByteReader): List<String> {
        val count = inp.readUnsignedShort()
        val values = ArrayList<String>(count)
        for (i in 0 until count) {
            values.add(IrReader.readLpString(inp))
        }
        return values
    }

    private fun decodeOverlayTail(inp: IrByteReader, guard: InlineDecodingGuard, version: Int): OverlayTail {
        if (!inp.isReadable()) {
            return OverlayTail.empty()
        }
        val descriptorCount = inp.readUnsignedShort()
        val descriptors = ArrayList<OverlayDescriptor>(descriptorCount)
        for (i in 0 until descriptorCount) {
            descriptors.add(decodeOverlayDescriptor(inp, guard, version))
        }
        val elementOverlayCount = inp.readUnsignedShort()
        val elementOverlays = LinkedHashMap<String, List<Int>>(elementOverlayCount)
        for (i in 0 until elementOverlayCount) {
            val elementId = IrReader.readLpString(inp)
            val overlayCount = inp.readUnsignedShort()
            val overlays = ArrayList<Int>(overlayCount)
            for (j in 0 until overlayCount) {
                overlays.add(inp.readUnsignedShort())
            }
            elementOverlays[elementId] = overlays
        }
        return OverlayTail(descriptors, elementOverlays)
    }

    private fun decodeOverlayDescriptor(inp: IrByteReader, guard: InlineDecodingGuard, version: Int): OverlayDescriptor {
        val kind = OverlayDescriptor.OverlayKind.fromOrdinal(inp.readUnsignedByte())
        val hostId = readOptionalString(inp)
        val anchor = OverlayDescriptor.AnchorMode.fromOrdinal(inp.readUnsignedByte())
        val contentRef = decodeContentRef(inp, guard)
        val interactive = inp.readUnsignedByte() != 0
        val animation = decodeAnimationSpec(inp, version)
        val dismissBehavior = decodeDismissBehavior(inp)
        val placement = decodePlacementSpec(inp)
        val dataBindings = decodeTooltipDataBindings(inp, version)
        return OverlayDescriptor(
            kind, hostId, anchor, contentRef, interactive,
            animation, dismissBehavior, placement, dataBindings,
        )
    }

    /** v30（ADR-0036）：解码 tooltip dataBindings；低版本字节流没有该段，视为空 list 前向降级。 */
    private fun decodeTooltipDataBindings(
        inp: IrByteReader,
        version: Int,
    ): List<OverlayDescriptor.TooltipDataBinding> {
        if (version < 30) {
            return emptyList()
        }
        val count = inp.readUnsignedShort()
        val list = ArrayList<OverlayDescriptor.TooltipDataBinding>(count)
        for (i in 0 until count) {
            val name = IrReader.readLpString(inp)
            val namespace = IrReader.readLpString(inp)
            val handler = IrReader.readLpString(inp)
            val payloadCount = inp.readUnsignedShort()
            val payload = LinkedHashMap<String, ByteArray>(payloadCount)
            for (j in 0 until payloadCount) {
                val key = IrReader.readLpString(inp)
                val blob = IrReader.readLpBytes32(inp)
                payload[key] = blob
            }
            // v31：可选 when 条件 blob；版本 < 31 时该字段不存在，视为无条件前向降级。
            var condition: ByteArray? = null
            if (version >= 31) {
                val hasCondition = inp.readUnsignedByte()
                if (hasCondition == 1) {
                    condition = IrReader.readLpBytes32(inp)
                }
            }
            list.add(OverlayDescriptor.TooltipDataBinding(name, namespace, handler, payload, condition))
        }
        return list
    }

    private fun decodeContentRef(inp: IrByteReader, guard: InlineDecodingGuard): ContentRef {
        val tag = inp.readUnsignedByte()
        return when (tag) {
            CR_TEXT -> ContentRef.Text(IrReader.readLpString(inp))
            CR_INLINE -> ContentRef.Inline(decodeInlineFromBytes(readInlineBytes(inp), guard))
            CR_COMPONENT -> {
                val name = IrReader.readLpString(inp)
                val propCount = inp.readUnsignedShort()
                val props = LinkedHashMap<String, Any?>(propCount)
                for (i in 0 until propCount) {
                    val key = IrReader.readLpString(inp)
                    props[key] = IrReader.readValue(inp)
                }
                ContentRef.Component(name, props)
            }
            else -> throw IllegalStateException("未知 ContentRef 类型: " + tag)
        }
    }

    private fun readInlineBytes(inp: IrByteReader): ByteArray {
        val len = inp.readInt()
        if (len < 0) {
            throw IllegalStateException("Inline IR 字节数为负: " + len)
        }
        if (len > MAX_INLINE_BYTES) {
            throw IllegalStateException("Inline IR 字节数超过限制: " + len)
        }
        val data = ByteArray(len)
        inp.readBytes(data)
        return data
    }

    private fun decodeAnimationSpec(inp: IrByteReader, version: Int): OverlayDescriptor.AnimationSpec {
        val fade = inp.readUnsignedByte() != 0
        val scale = inp.readUnsignedByte() != 0
        val durationMs = inp.readDouble()
        val fadeOutMs = if (version >= 18) inp.readDouble() else durationMs
        val easing = IrReader.readLpString(inp)
        return OverlayDescriptor.AnimationSpec(fade, scale, durationMs, fadeOutMs, easing)
    }

    private fun decodeDismissBehavior(inp: IrByteReader): OverlayDescriptor.DismissBehavior {
        return OverlayDescriptor.DismissBehavior.fromMask(inp.readUnsignedShort())
    }

    private fun decodePlacementSpec(inp: IrByteReader): OverlayDescriptor.PlacementSpec {
        val direction = OverlayDescriptor.PlacementSpec.Direction.fromOrdinal(inp.readUnsignedByte())
        val padding = inp.readDouble()
        val offsetX = inp.readDouble()
        val offsetY = inp.readDouble()
        return OverlayDescriptor.PlacementSpec(direction, padding, offsetX, offsetY)
    }

    private class OverlayTail(
        val overlayDescriptors: List<OverlayDescriptor>,
        val elementOverlays: Map<String, List<Int>>,
    ) {
        companion object {
            private val EMPTY = OverlayTail(emptyList(), emptyMap())

            fun empty(): OverlayTail = EMPTY
        }
    }

    // ==================== 子结构解码 ====================

    private fun decodeStateMachine(id: String, inp: IrByteReader): UiStateMachine {
        val initialState = IrReader.readLpString(inp)

        val stateCount = inp.readUnsignedShort()
        val states = LinkedHashMap<String, UiSmState>(stateCount)
        for (i in 0 until stateCount) {
            val name = IrReader.readLpString(inp)
            val propCount = inp.readUnsignedShort()
            val props = LinkedHashMap<String, UiBinding>(propCount)
            for (j in 0 until propCount) {
                val key = IrReader.readLpString(inp)
                props[key] = decodeBinding(inp)
            }
            states[name] = UiSmState(name, props)
        }

        val transitionCount = inp.readUnsignedShort()
        val transitions = ArrayList<UiSmTransition>(transitionCount)
        for (i in 0 until transitionCount) {
            val fromRaw = IrReader.readLpString(inp)
            val from = if (fromRaw.isEmpty()) null else fromRaw
            val to = IrReader.readLpString(inp)
            val durationMs = inp.readUnsignedInt()
            val delayMs = inp.readUnsignedInt()
            val easing = IrReader.readLpString(inp)
            transitions.add(UiSmTransition(from, to, durationMs, delayMs, easing))
        }

        return UiStateMachine(id, initialState, states, transitions)
    }

    private fun decodeFunction(name: String, inp: IrByteReader): IrFun {
        val paramCount = inp.readUnsignedShort()
        val params = ArrayList<String>(paramCount)
        // M5-b（v11）：交错读取默认值 blob。
        val defaults = ArrayList<ByteArray?>(paramCount)
        var anyDefault = false
        for (i in 0 until paramCount) {
            params.add(IrReader.readLpString(inp))
            val hasDefault = inp.readUnsignedByte()
            if (hasDefault != 0) {
                defaults.add(IrReader.readLpBytes32(inp))
                anyDefault = true
            } else {
                defaults.add(null)
            }
        }
        val body = IrReader.readLpBytes32(inp)
        // 全部无默认值时传空列表，保持等价。
        val defaultsForCtor: List<ByteArray?> = if (anyDefault) defaults else emptyList()
        return IrFun(name, params, body, defaultsForCtor)
    }

    private fun decodeThemeValue(inp: IrByteReader): Any? {
        val tag = inp.readUnsignedByte()
        return when (tag) {
            TT_STRING -> IrReader.readLpString(inp)
            TT_NUMBER -> inp.readDouble()
            TT_BOOL -> inp.readByte().toInt() != 0
            else -> throw IllegalStateException("未知 themeToken valueTag: " + tag)
        }
    }

    /** v12：读取一个可空 ExprBlob；hasExpr=0 时返回 null。 */
    private fun readOptionalExprBlob(inp: IrByteReader): ByteArray? {
        return readOptionalBytes(inp)
    }

    /** 读取一个可空字节段；has=0 时返回 null。 */
    private fun readOptionalBytes(inp: IrByteReader): ByteArray? {
        val has = inp.readUnsignedByte()
        if (has == 0) {
            return null
        }
        if (has != 1) {
            throw IllegalStateException("非法可空字节段标记: " + has)
        }
        return IrReader.readLpBytes32(inp)
    }

    /** 读取一个可空字符串；has=0 时返回 null。 */
    private fun readOptionalString(inp: IrByteReader): String? {
        val has = inp.readUnsignedByte()
        if (has == 0) {
            return null
        }
        if (has != 1) {
            throw IllegalStateException("非法可空字符串标记: " + has)
        }
        return IrReader.readLpString(inp)
    }

    private fun decodeNode(inp: IrByteReader, version: Int, guard: InlineDecodingGuard): UiNode {
        // 进入一层节点递归并计数；出口在 finally 中配对递减，确保异常路径不泄漏计数。
        guard.enterNode()
        try {
            return decodeNodeBody(inp, version, guard)
        } finally {
            guard.exitNode()
        }
    }

    private fun decodeNodeBody(inp: IrByteReader, version: Int, guard: InlineDecodingGuard): UiNode {
        val kind = IrReader.readLpString(inp)
        val id = IrReader.readLpString(inp)

        val propCount = inp.readUnsignedShort()
        val props = LinkedHashMap<String, UiBinding>(propCount)
        for (i in 0 until propCount) {
            val key = IrReader.readLpString(inp)
            props[key] = decodeBinding(inp)
        }

        val animationRefCount = inp.readUnsignedShort()
        val animationRefs = LinkedHashMap<String, String>(animationRefCount)
        for (i in 0 until animationRefCount) {
            val trigger = IrReader.readLpString(inp)
            val animationId = IrReader.readLpString(inp)
            animationRefs[trigger] = animationId
        }

        val childCount = inp.readUnsignedShort()
        val children = ArrayList<UiNode>(childCount)
        for (i in 0 until childCount) {
            children.add(decodeNode(inp, version, guard))
        }

        // v9 canvas 段；v8 字节流不含此字节，默认 null
        var canvasData: UiCanvasData? = null
        if (version >= 9) {
            val hasCanvas = inp.readUnsignedByte()
            if (hasCanvas == 1) {
                val width = inp.readUnsignedShort()
                val height = inp.readUnsignedShort()
                val blobLen = inp.readUnsignedShort()
                val blob = ByteArray(blobLen)
                inp.readBytes(blob)
                // v10 起携带 durationMs + loopMode；v9 字节流走默认值
                var durationMs = UiCanvasData.DEFAULT_DURATION_MS
                var loopMode: String? = UiCanvasData.DEFAULT_LOOP_MODE
                if (version >= 10) {
                    durationMs = inp.readDouble()
                    val raw = IrReader.readLpString(inp)
                    loopMode = if (raw.isEmpty()) null else raw
                }
                // v12 起携带 widthExpr/heightExpr/durationExpr 三段可空 ExprBlob
                var widthExpr: ByteArray? = null
                var heightExpr: ByteArray? = null
                var durationExpr: ByteArray? = null
                if (version >= 12) {
                    widthExpr = readOptionalExprBlob(inp)
                    heightExpr = readOptionalExprBlob(inp)
                    durationExpr = readOptionalExprBlob(inp)
                }
                canvasData = UiCanvasData(
                    width, height, blob, durationMs, loopMode,
                    widthExpr, heightExpr, durationExpr,
                )
            } else if (hasCanvas != 0) {
                throw IllegalStateException("非法 hasCanvas 标记: " + hasCanvas)
            }
        }

        // v12 source 段（M6 数据驱动容器）
        // MIN_SUPPORTED=12 起该字节恒存在；hasSource=0 仅占 1 字节。
        var sourceExpr: ByteArray? = null
        var sourceParams: List<String> = emptyList()
        val hasSource = inp.readUnsignedByte()
        if (hasSource == 1) {
            sourceExpr = IrReader.readLpBytes32(inp)
            val paramCount = inp.readUnsignedByte()
            if (paramCount != 1 && paramCount != 2) {
                throw IllegalStateException("非法 sourceParams paramCount: " + paramCount)
            }
            val params = ArrayList<String>(paramCount)
            for (i in 0 until paramCount) {
                params.add(IrReader.readLpString(inp))
            }
            sourceParams = params
        } else if (hasSource != 0) {
            throw IllegalStateException("非法 hasSource 标记: " + hasSource)
        }

        var contentRef: ContentRef? = null
        if (version >= 19) {
            val hasContentRef = inp.readUnsignedByte()
            if (hasContentRef == 1) {
                contentRef = decodeContentRef(inp, guard)
            } else if (hasContentRef != 0) {
                throw IllegalStateException("非法 hasContentRef 标记: " + hasContentRef)
            }
        }

        // RFC-RECOMPOSITION（v20 起）：selector 表达式 blob。
        // hasSelector=0 表示普通节点；hasSelector=1 时跟随 u32 blobLen + bytes。
        var selectorExpr: ByteArray? = null
        if (version >= 20) {
            val hasSelector = inp.readUnsignedByte()
            if (hasSelector == 1) {
                selectorExpr = IrReader.readLpBytes32(inp)
            } else if (hasSelector != 0) {
                throw IllegalStateException("非法 hasSelector 标记: " + hasSelector)
            }
        }

        // $for（v26 起）：for 容器节点的可迭代表达式 + 循环变量。
        // hasFor=0 表示普通节点；hasFor=1 时跟随 u32 iterableBlobLen + bytes
        // + u8 forOverMap + LP-UTF8 var1 + u8 hasVar2 (+ LP-UTF8 var2)
        // + v28 追加 u8 hasKeyExpr (+ u32 keyExpr blobLen + bytes)。
        var iterableExpr: ByteArray? = null
        var forVar1: String? = null
        var forVar2: String? = null
        var forOverMap = false
        var keyExpr: ByteArray? = null
        if (version >= 26) {
            val hasFor = inp.readUnsignedByte()
            if (hasFor == 1) {
                iterableExpr = IrReader.readLpBytes32(inp)
                forOverMap = inp.readUnsignedByte() != 0
                forVar1 = IrReader.readLpString(inp)
                val hasVar2 = inp.readUnsignedByte()
                if (hasVar2 == 1) {
                    forVar2 = IrReader.readLpString(inp)
                } else if (hasVar2 != 0) {
                    throw IllegalStateException("非法 hasVar2 标记: " + hasVar2)
                }
                // v28：显式 key 表达式（版本 < 28 的字节流无此字节，视为 null 前向降级）
                if (version >= 28) {
                    val hasKeyExpr = inp.readUnsignedByte()
                    if (hasKeyExpr == 1) {
                        keyExpr = IrReader.readLpBytes32(inp)
                    } else if (hasKeyExpr != 0) {
                        throw IllegalStateException("非法 hasKeyExpr 标记: " + hasKeyExpr)
                    }
                }
            } else if (hasFor != 0) {
                throw IllegalStateException("非法 hasFor 标记: " + hasFor)
            }
        }

        // v29：布局动画（FLIP）标记位（版本 < 29 的字节流无此字节，视为 false 前向降级）
        var animateLayout = false
        if (version >= 29) {
            val flag = inp.readUnsignedByte()
            if (flag == 1) {
                animateLayout = true
            } else if (flag != 0) {
                throw IllegalStateException("非法 animateLayout 标记: " + flag)
            }
        }

        val modifiers = decodeModifiers(inp, version)

        return UiNode(
            kind, id, props, animationRefs, children, canvasData,
            sourceExpr, sourceParams, contentRef, selectorExpr,
            iterableExpr, forVar1, forVar2, forOverMap, keyExpr, animateLayout, modifiers,
        )
    }

    /** v32：读取 UiNode 末尾的视觉 modifier 链段。 */
    private fun decodeModifiers(inp: IrByteReader, version: Int): List<UiModifier> {
        if (version < 32) {
            return emptyList()
        }
        val count = inp.readUnsignedShort()
        if (count == 0) {
            return emptyList()
        }
        val modifiers = ArrayList<UiModifier>(count)
        for (i in 0 until count) {
            val kind = ModifierOpcodes.opcodeToKind(inp.readUnsignedByte())
            val fieldCount = inp.readUnsignedShort()
            val fields = LinkedHashMap<String, UiBinding>(fieldCount)
            for (j in 0 until fieldCount) {
                val field = IrReader.readLpString(inp)
                fields[field] = decodeBinding(inp)
            }
            modifiers.add(UiModifier(kind, fields))
        }
        return modifiers
    }

    private fun decodeAnimation(inp: IrByteReader, version: Int, guard: InlineDecodingGuard): UiAnimation {
        // 进入一层动画递归并计数；出口在 finally 中配对递减，确保异常路径不泄漏计数。
        guard.enterNode()
        try {
            return decodeAnimationBody(inp, version, guard)
        } finally {
            guard.exitNode()
        }
    }

    private fun decodeAnimationBody(inp: IrByteReader, version: Int, guard: InlineDecodingGuard): UiAnimation {
        val kindValue = inp.readUnsignedByte()
        val kinds = UiAnimation.Kind.values()
        if (kindValue > ANIMATION_KIND_V4_MAX || kindValue >= kinds.size) {
            throw IllegalStateException("未知 animation kind: " + kindValue)
        }

        val durationMs = inp.readDouble()
        val delayMs = inp.readDouble()
        val staggerMs = inp.readDouble()
        val easing = IrReader.readLpString(inp)

        val propCount = inp.readUnsignedShort()
        val props = LinkedHashMap<String, UiBinding>(propCount)
        for (i in 0 until propCount) {
            val key = IrReader.readLpString(inp)
            props[key] = decodeBinding(inp)
        }

        val keyframeCount = inp.readUnsignedShort()
        val keyframes = ArrayList<UiKeyframe>(keyframeCount)
        for (i in 0 until keyframeCount) {
            val offset = inp.readDouble()
            val keyframePropCount = inp.readUnsignedShort()
            val keyframeProps = LinkedHashMap<String, UiBinding>(keyframePropCount)
            for (j in 0 until keyframePropCount) {
                val key = IrReader.readLpString(inp)
                keyframeProps[key] = decodeBinding(inp)
            }
            keyframes.add(UiKeyframe(offset, keyframeProps))
        }

        val childCount = inp.readUnsignedShort()
        val children = ArrayList<UiAnimation>(childCount)
        for (i in 0 until childCount) {
            children.add(decodeAnimation(inp, version, guard))
        }

        // v8: 读取 loopMode 字段
        var loopMode: String? = null
        if (version >= 8) {
            val raw = IrReader.readLpString(inp)
            if (raw.isNotEmpty()) {
                loopMode = raw
            }
        }

        // v29: 读取 spring 物理参数（版本 < 29 的字节流无此段，视为 null 前向降级）
        var spring: SpringSpec? = null
        if (version >= 29) {
            val hasSpring = inp.readUnsignedByte()
            if (hasSpring == 1) {
                val stiffness = inp.readDouble()
                val damping = inp.readDouble()
                val mass = inp.readDouble()
                val initialVelocity = inp.readDouble()
                spring = SpringSpec(stiffness, damping, mass, initialVelocity)
            } else if (hasSpring != 0) {
                throw IllegalStateException("非法 hasSpring 标记: " + hasSpring)
            }
        }

        return UiAnimation(
            kinds[kindValue], durationMs, delayMs, staggerMs, easing, props, keyframes, children, loopMode, spring,
        )
    }

    private fun decodeBinding(inp: IrByteReader): UiBinding {
        val bt = inp.readUnsignedByte()
        return when (bt) {
            BT_LITERAL -> UiBinding.literal(IrReader.readValue(inp))
            BT_EXPR -> UiBinding.expr(IrReader.readLpBytes32(inp))
            BT_EVENT -> UiBinding.event(IrReader.readLpString(inp))
            else -> throw IllegalStateException("未知 bindingType: " + bt)
        }
    }

    // ==================== bytes 便利方法 ====================

    private fun decodeFromBytes(data: ByteArray, guard: InlineDecodingGuard): UiIr {
        return decode(ByteArrayIrReader(data), guard)
    }

    private fun decodeInlineFromBytes(data: ByteArray, guard: InlineDecodingGuard): UiIr {
        if (data.size > MAX_INLINE_BYTES) {
            throw IllegalStateException("Inline IR 字节数超过限制: " + data.size)
        }
        guard.enterInline()
        try {
            return decodeFromBytes(data, guard)
        } finally {
            guard.exitInline()
        }
    }

    private class InlineDecodingGuard {
        private var inlineDepth = 0
        private var nodeDepth = 0

        fun enterInline() {
            if (inlineDepth >= MAX_INLINE_DEPTH) {
                throw IllegalStateException("Inline IR 嵌套层数超过限制: " + (inlineDepth + 1))
            }
            inlineDepth++
        }

        fun exitInline() {
            inlineDepth--
        }

        /** 进入一层节点 / 动画递归；超过上限时立即拒绝，避免栈溢出。 */
        fun enterNode() {
            if (nodeDepth >= MAX_NODE_DEPTH) {
                throw IllegalStateException(
                    "IR 节点嵌套深度超过限制: " + (nodeDepth + 1) + " > " + MAX_NODE_DEPTH,
                )
            }
            nodeDepth++
        }

        fun exitNode() {
            nodeDepth--
        }
    }
}
